import { type AwardedBadge, type BadgesResponse } from './badges';

/**
 * Total badges earned — the full sum of every `awarded_count` (repeats counted).
 * Drives the "Total Badges Earned" stat.
 */
export const countTotalBadgesEarned = (res: BadgesResponse): number => {
  let total = 0;
  for (const badge of res.awarded_badges) {
    total += badge.awarded_count;
  }
  return total;
};

/**
 * The module of the most recently awarded module-bound badge — i.e. the latest
 * module the student earned a badge in. Returns undefined when no module-bound
 * badge has been earned.
 */
export const getLatestEarnedModuleId = (
  res: BadgesResponse
): string | undefined => {
  let latest: AwardedBadge | undefined;
  for (const badge of res.awarded_badges) {
    if (!badge.module_id) continue;
    if (!latest || badge.awarded_on > latest.awarded_on) {
      latest = badge;
    }
  }
  return latest?.module_id;
};

/**
 * Total badge awards earned in a given module — sums the per-module
 * `awarded_count` across every badge type, so repeated awards are included.
 */
export const countModuleBadges = (
  res: BadgesResponse,
  moduleId: string
): number => {
  let count = 0;
  for (const badge of res.awarded_badges) {
    if (badge.module_awards && badge.module_awards.length > 0) {
      const award = badge.module_awards.find(a => a.module_id === moduleId);
      if (award) count += award.awarded_count;
    } else if (badge.module_id === moduleId) {
      count += badge.awarded_count;
    }
  }
  return count;
};

/**
 * The module ID with the highest module number among all awarded module-bound
 * badges. Uses `moduleNumberById` (from the modules API) to resolve numbers;
 * falls back to `getLatestEarnedModuleId` when no number data is available.
 */
export const getHighestNumberedModuleId = (
  res: BadgesResponse,
  moduleNumberById: Record<string, string>
): string | undefined => {
  let highestId: string | undefined;
  let highestNumber = -Infinity;
  const seenModuleIds = new Set<string>();

  for (const badge of res.awarded_badges) {
    const ids: string[] = [];
    if (badge.module_awards && badge.module_awards.length > 0) {
      ids.push(...badge.module_awards.map(a => a.module_id).filter(Boolean));
    } else if (badge.module_id) {
      ids.push(badge.module_id);
    }
    for (const id of ids) {
      if (seenModuleIds.has(id)) continue;
      seenModuleIds.add(id);
      const num = Number.parseInt(moduleNumberById[id] ?? '', 10);
      if (!Number.isNaN(num) && num > highestNumber) {
        highestNumber = num;
        highestId = id;
      }
    }
  }

  return highestId ?? getLatestEarnedModuleId(res);
};

/**
 * "Module Badges" stat: the badge count for the highest-numbered module the
 * student has earned badges in. 0 until they earn their first module-bound badge.
 */
export const countLatestModuleBadges = (
  res: BadgesResponse,
  moduleNumberById: Record<string, string> = {}
): number => {
  const moduleId = getHighestNumberedModuleId(res, moduleNumberById);
  return moduleId ? countModuleBadges(res, moduleId) : 0;
};
